import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { formatINR } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminVenuePricing = () => {
  const { getToken } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/venue-pricing`);
      setPricing(response.data);
    } catch (error) {
      toast.error("Failed to load venue pricing");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/venue-pricing`, pricing, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPricing(response.data);
      toast.success("Venue pricing updated successfully");
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setPricing(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  if (loading) {
    return (
      <AdminLayout title="Venue Pricing">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Venue Pricing">
      <div className="max-w-4xl space-y-8" data-testid="venue-pricing-page">
        
        {/* Base Prices */}
        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
          <h2 className="text-xl font-serif font-medium mb-2">Base Venue Prices</h2>
          <p className="text-sm text-muted-foreground mb-6">All prices are inclusive of GST</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="form-group">
              <Label htmlFor="price_24hr">24 Hours Package</Label>
              <Input
                id="price_24hr"
                data-testid="price-24hr-input"
                type="number"
                value={pricing?.price_24hr || 0}
                onChange={(e) => handleChange('price_24hr', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Current: {formatINR(pricing?.price_24hr)}
              </p>
            </div>
            
            <div className="form-group">
              <Label htmlFor="price_48hr">48 Hours Package</Label>
              <Input
                id="price_48hr"
                data-testid="price-48hr-input"
                type="number"
                value={pricing?.price_48hr || 0}
                onChange={(e) => handleChange('price_48hr', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Current: {formatINR(pricing?.price_48hr)}
              </p>
            </div>
            
            <div className="form-group">
              <Label htmlFor="price_72hr">72 Hours Package</Label>
              <Input
                id="price_72hr"
                data-testid="price-72hr-input"
                type="number"
                value={pricing?.price_72hr || 0}
                onChange={(e) => handleChange('price_72hr', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Current: {formatINR(pricing?.price_72hr)}
              </p>
            </div>
          </div>
        </div>

        {/* Advance Payment Discounts */}
        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
          <h2 className="text-xl font-serif font-medium mb-2">Payment Mode Discounts</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Discount applies on Total Deal Value (Venue + Food)
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="form-group">
              <Label htmlFor="discount_100">100% Upfront Discount</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="discount_100"
                  data-testid="discount-100-input"
                  type="number"
                  value={pricing?.discount_100_advance || 0}
                  onChange={(e) => handleChange('discount_100_advance', e.target.value)}
                />
                <span className="text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Default: 35%</p>
            </div>
            
            <div className="form-group">
              <Label htmlFor="discount_75">75% Upfront Discount</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="discount_75"
                  data-testid="discount-75-input"
                  type="number"
                  value={pricing?.discount_75_advance || 0}
                  onChange={(e) => handleChange('discount_75_advance', e.target.value)}
                />
                <span className="text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Default: 25%</p>
            </div>
            
            <div className="form-group">
              <Label htmlFor="discount_50">50% Advance Discount</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="discount_50"
                  data-testid="discount-50-input"
                  type="number"
                  value={pricing?.discount_50_advance || 0}
                  onChange={(e) => handleChange('discount_50_advance', e.target.value)}
                />
                <span className="text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Default: 10% (confirms booking)</p>
            </div>
          </div>
        </div>

        {/* Accommodation Rules */}
        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
          <h2 className="text-xl font-serif font-medium mb-2">Accommodation Rules</h2>
          <p className="text-sm text-muted-foreground mb-6">Extra charges are inclusive of GST</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-group">
              <Label htmlFor="included_pax">Included Pax (Base)</Label>
              <Input
                id="included_pax"
                data-testid="included-pax-input"
                type="number"
                value={pricing?.included_pax || 0}
                onChange={(e) => handleChange('included_pax', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Guests included in base price
              </p>
            </div>
            
            <div className="form-group">
              <Label htmlFor="extra_pax_charge">Extra Pax Charge (per day, incl. GST)</Label>
              <Input
                id="extra_pax_charge"
                data-testid="extra-pax-charge-input"
                type="number"
                value={pricing?.extra_pax_charge_per_day || 0}
                onChange={(e) => handleChange('extra_pax_charge_per_day', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Per person per day for guests above included pax
              </p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-venue-pricing-btn"
            className="bg-[#B08968] hover:bg-[#9A7558] text-white px-8"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminVenuePricing;
